import { domainColor, type SatDomain } from '@/api/sat';
import { cn } from '@/lib/utils';
import { STUDENT_BAND_STROKE_CLASSES } from './studentCopy';

/**
 * Per-stop progress ring: band-colored arc for the domain's evidence score.
 * Shared by the personalized trail and the first-run course preview.
 */
export function StopRing({
  scorePct,
  band,
  children,
  unearned = false,
  fill,
  tone,
}: {
  scorePct: number | null;
  band: keyof typeof STUDENT_BAND_STROKE_CLASSES;
  children: React.ReactNode;
  /** Preview only: dashed empty track — "nothing earned here yet", not "zero". */
  unearned?: boolean;
  /** Preview only: animate the arc (`ms`, after `delayMs`) instead of the default ease. */
  fill?: { ms: number; delayMs: number } | 'instant';
  /** Homepage only: the area's own colour as a soft inner tint and a tinted track. Arcs stay band-coloured. */
  tone?: SatDomain;
}) {
  const pct = Math.max(0, Math.min(100, scorePct ?? 0));
  const preview = fill !== undefined;
  return (
    <span className="relative flex h-16 w-16 items-center justify-center">
      <svg viewBox="0 0 64 64" className="absolute inset-0 h-full w-full -rotate-90" aria-hidden>
        {tone && <circle cx="32" cy="32" r="25" fill={domainColor(tone, 0.12)} data-testid="stop-ring-tint" />}
        <circle
          cx="32" cy="32" r="28" fill="none" strokeWidth="5"
          strokeDasharray={unearned && pct === 0 ? '2.5 6.3' : undefined}
          className={tone ? undefined : unearned && pct === 0 ? 'stroke-muted-foreground/30' : 'stroke-muted'}
          style={tone ? { stroke: domainColor(tone, unearned && pct === 0 ? 0.65 : 0.22) } : undefined}
        />
        {(pct > 0 || preview) && (
          <circle
            cx="32" cy="32" r="28" fill="none" strokeWidth="5" strokeLinecap="round"
            pathLength={100}
            data-testid={preview ? 'stop-ring-arc' : undefined}
            data-pct={preview ? pct : undefined}
            className={cn(!preview && 'transition-all duration-700', STUDENT_BAND_STROKE_CLASSES[band])}
            style={
              preview
                ? {
                    strokeDasharray: `${pct} 100`,
                    opacity: pct > 0 ? 1 : 0,
                    transition:
                      fill === 'instant' || fill === undefined
                        ? 'none'
                        : `stroke-dasharray ${fill.ms}ms ease-out ${fill.delayMs}ms, stroke 300ms linear ${fill.delayMs}ms, opacity 120ms linear ${fill.delayMs}ms`,
                  }
                : { strokeDasharray: `${pct} 100` }
            }
          />
        )}
      </svg>
      {children}
    </span>
  );
}
